import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Clock, Heart, Check, X, Star } from 'lucide-react';
import { Location, UserLocation } from '../types';
import { getCachedRoute } from '../utils/routes';
import { getImageUrl } from '../utils/images';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { checkFavoriteStatus, toggleFavorite, markVisited, getVisitStatus, updateVisitReview, getVisitStats } from '../utils/supabase';
import toast from 'react-hot-toast';

interface LocationCardProps {
  location: Location;
  userLocation?: UserLocation | null;
  onSelect: (location: Location) => void;
  isLoading?: boolean;
  isCompact?: boolean;
  onFavoriteChange?: () => void;
}

export const LocationCard: React.FC<LocationCardProps> = ({
  location,
  userLocation,
  onSelect,
  isLoading,
  isCompact,
  onFavoriteChange
}) => {
  const { user } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [distance, setDistance] = useState<number | null>(null);
  const [driveTime, setDriveTime] = useState<number | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [visitStatus, setVisitStatus] = useState<{ liked: boolean | null } | null>(null);
  const [stats, setStats] = useState<{ total: number; likes: number; dislikes: number } | null>(null);

  useEffect(() => {
    if (user && location.id) {
      checkFavoriteStatus(location.id)
        .then(setIsFavorite)
        .catch(console.error);
    }
  }, [user, location.id]);

  useEffect(() => {
    if (location.id) {
      getVisitStats(location.id).then(setStats).catch(console.error);
    }
    if (user && location.id) {
      getVisitStatus(location.id).then(setVisitStatus).catch(console.error);
    }
  }, [user, location.id]);

  useEffect(() => {
    if (userLocation && location.coordinates) {
      getCachedRoute(userLocation, location.coordinates)
        .then(({ distance, duration }) => {
          setDistance(distance);
          setDriveTime(duration);
        })
        .catch(console.error);
    }
  }, [userLocation, location.coordinates]);

  useEffect(() => {
    if (isHovered || isTouched) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => 
          prev === (location.images?.length ?? 1) - 1 ? 0 : prev + 1
        );
      }, 2500);
      return () => clearInterval(interval);
    }
    return () => setCurrentImageIndex(0);
  }, [isHovered, isTouched, location.images?.length]);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error('Каб дадаць у абранае, калі ласка, увайдзіце ў сістэму');
      return;
    }

    if (!location.id) {
      toast.error('Памылка: няправільны ідэнтыфікатар лакацыі');
      return;
    }

    try {
      setIsTogglingFavorite(true);
      const { error } = await toggleFavorite(location.id, isFavorite);
      
      if (error) throw error;

      setIsFavorite(!isFavorite);
      toast.success(isFavorite ? 'Выдалена з абранага' : 'Дададзена ў абранае');
      
      if (onFavoriteChange) {
        onFavoriteChange();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Не атрымалася змяніць статус абранага');
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const handleVisit = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error('Каб адзначыць, калі ласка, увайдзіце ў сістэму');
      return;
    }

    if (!location.id) return;
    const { error } = await markVisited(location.id);
    if (!error) {
      const status = await getVisitStatus(location.id);
      setVisitStatus(status);
      setStats(await getVisitStats(location.id));
    }
  };

  const handleReview = async (liked: boolean) => {
    if (!location.id) return;
    const { error } = await updateVisitReview(location.id, liked);
    if (!error) {
      setVisitStatus({ liked });
      setStats(await getVisitStats(location.id));
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse border border-gray-100">
        <div className="aspect-[4/3] bg-gray-200" />
        <div className="p-5 space-y-4">
          <div className="h-6 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-16 bg-gray-200 rounded" />
          <div className="flex gap-2">
            <div className="h-6 bg-gray-200 rounded w-20" />
            <div className="h-6 bg-gray-200 rounded w-20" />
            <div className="h-6 bg-gray-200 rounded w-20" />
          </div>
        </div>
      </div>
    );
  }

  const formatDuration = (minutes: number) => {
    if (minutes > 60) {
      const hours = minutes / 60;
      const roundedHours = Math.round(hours * 10) / 10;
      return `${Number.isInteger(roundedHours) ? Math.floor(roundedHours) : roundedHours} г.`;
    }
    return `${minutes} хв`;
  };

  const images = location.images ?? [];
  const tags = location.tags ?? [];

  // Debug logging for images
  console.log('LocationCard rendering for:', location.name);
  console.log('Images array:', images);
  console.log('Current image index:', currentImageIndex);
  console.log('Current image URL:', images[currentImageIndex]);

  return (
    <Link 
      to={`/l/${location.short_id}`}
      className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 hover:border-primary/20 flex flex-col relative transform hover:-translate-y-1"
      onClick={(e) => {
        e.preventDefault();
        onSelect(location);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => setIsTouched(true)}
      onTouchEnd={() => setIsTouched(false)}
    >
      {user && (
        <button
          onClick={handleFavoriteClick}
          disabled={isTogglingFavorite}
          className={`absolute top-3 right-3 z-10 p-2.5 rounded-full transition-all duration-300 backdrop-blur-sm ${
            isFavorite 
              ? 'bg-red-500 text-white shadow-lg' 
              : 'bg-white/90 hover:bg-white text-gray-600 hover:text-red-500 shadow-md'
          } ${isTogglingFavorite ? 'opacity-50' : 'hover:scale-110'}`}
        >
          <Heart
            size={18}
            className={isFavorite ? 'fill-current' : ''}
          />
        </button>
      )}

      {/* Image Section */}
      <div className="relative overflow-hidden">
        {images.length > 0 ? (
          <div className="aspect-[4/3] relative">
            <img 
              src={getImageUrl(images[currentImageIndex])} 
              alt={location.name}
              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
              onError={(e) => {
                console.error('Image failed to load:', images[currentImageIndex]);
                console.error('Processed URL:', getImageUrl(images[currentImageIndex]));
                // Try to show a placeholder or hide the image
                e.currentTarget.style.display = 'none';
              }}
              onLoad={() => {
                console.log('Image loaded successfully:', images[currentImageIndex]);
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        ) : (
          <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <p className="text-gray-500 text-sm font-medium">Няма фотаздымкаў</p>
          </div>
        )}
        
        {images.length > 1 && (
          <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs font-medium">
            {currentImageIndex + 1}/{images.length}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-5 flex flex-col flex-1 space-y-4">
        {/* Title and Location */}
        <div>
          <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2 leading-tight group-hover:text-primary transition-colors duration-300">
            {location.name}
          </h3>
          {location.coordinates && (
            <div className="flex items-center text-primary text-xs mb-3">
              <MapPin size={14} className="mr-1.5 flex-shrink-0" />
              <span className="truncate font-mono">
                {location.coordinates.lat.toFixed(4)}, {location.coordinates.lng.toFixed(4)}
              </span>
            </div>
          )}
        </div>

        {/* Distance and Time */}
        {!isCompact && distance !== null && driveTime !== null && (
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1.5">
              <Navigation size={16} className="flex-shrink-0 text-primary" />
              <span className="font-medium">{distance} км</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock size={16} className="flex-shrink-0 text-primary" />
              <span className="font-medium">{formatDuration(driveTime)}</span>
            </div>
          </div>
        )}
        
        {/* Description */}
        <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed flex-grow">
          {location.shortDescription}
        </p>
        
        {/* Tags and Stats */}
        {!isCompact && (
          <div className="mt-auto space-y-4">
            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary/10 text-xs text-primary font-medium border border-primary/20"
                >
                  #{tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="text-xs text-gray-500 px-2 py-1.5 bg-gray-100 rounded-full">
                  +{tags.length - 3}
                </span>
              )}
            </div>

            {/* Stats */}
            {stats && (
              <div className="text-xs text-gray-600 flex flex-wrap gap-3 pt-3 border-t border-gray-100">
                <span className="font-medium">Былі: {stats.total}</span>
                <span className="text-green-600 font-medium">Спадабалася: {stats.likes}</span>
                <span className="text-red-600 font-medium">Не спадабалася: {stats.dislikes}</span>
                <span className="text-primary font-semibold">
                  Задаволеныя: {stats.total ? Math.round((stats.likes / stats.total) * 100) : 0}%
                </span>
              </div>
            )}

            {/* User Actions */}
            {user && !visitStatus && (
              <button
                onClick={handleVisit}
                className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Быў тут
              </button>
            )}
            
            {user && visitStatus && visitStatus.liked === null && (
              <div className="space-y-2">
                <p className="text-xs text-gray-600 font-medium">Ці спадабалася вам гэтае месца?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleReview(true)}
                    className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition-colors"
                  >
                    Спадабалася
                  </button>
                  <button
                    onClick={() => handleReview(false)}
                    className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors"
                  >
                    Не спадабалася
                  </button>
                </div>
              </div>
            )}
            
            {user && visitStatus && visitStatus.liked !== null && (
              <div className="text-xs flex items-center justify-between pt-2 border-t border-gray-100">
                <span className={`font-medium ${visitStatus.liked ? 'text-green-600' : 'text-red-600'}`}>
                  {visitStatus.liked ? 'Спадабалася' : 'Не спадабалася'}
                </span>
                <button
                  onClick={() => setVisitStatus({ liked: null })}
                  className="text-primary hover:text-primary/80 underline font-medium"
                >
                  Змяніць
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};